import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Check, SignOut } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PreConsultaOnboarding({ onComplete }) {
  const { user, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [template, setTemplate] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const pt = user?.patient_type || 'geral';
        const { data } = await axios.get(`${API}/api/pre-consultation/template/${pt}`);
        setTemplate(data);
      } catch {} finally { setLoading(false); }
    })();
  }, [user]);

  const questions = template?.questions || [];
  const requiredQs = questions.filter(q => q.required);
  const allRequiredFilled = requiredQs.every(q => {
    const val = responses[q.label];
    if (Array.isArray(val)) return val.length > 0;
    return val !== undefined && val !== '';
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const resArray = questions.map(q => ({ label: q.label, type: q.type, value: responses[q.label] || '' }));
      await axios.post(`${API}/api/pre-consultation/submit`, {
        conditions_accepted_at: new Date().toISOString(),
        responses: resArray,
      });
      toast.success('Questionário concluído');
      if (onComplete) onComplete();
    } catch { toast.error('Erro ao submeter'); }
    finally { setSubmitting(false); }
  };

  const renderQuestion = (q, i) => {
    const val = responses[q.label] || (q.type === 'multi_choice' ? [] : '');
    switch (q.type) {
      case 'short_text':
        return <Input value={val} onChange={e => setResponses({ ...responses, [q.label]: e.target.value })} className="rounded-xl border-brand-border" />;
      case 'long_text':
        return <Textarea value={val} onChange={e => setResponses({ ...responses, [q.label]: e.target.value })} className="rounded-xl border-brand-border resize-none" rows={3} />;
      case 'number':
        return <Input type="number" value={val} onChange={e => setResponses({ ...responses, [q.label]: e.target.value })} className="rounded-xl border-brand-border w-32" />;
      case 'date':
        return <Input type="date" value={val} onChange={e => setResponses({ ...responses, [q.label]: e.target.value })} className="rounded-xl border-brand-border w-44" />;
      case 'scale':
        return (
          <div className="flex gap-1.5">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} type="button" onClick={() => setResponses({ ...responses, [q.label]: n })}
                className={`w-8 h-8 rounded-lg text-xs font-medium ${val === n ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
                {n}
              </button>
            ))}
          </div>
        );
      case 'yes_no':
        return (
          <div className="flex gap-2">
            {['Sim', 'Não'].map(opt => (
              <button key={opt} type="button" onClick={() => setResponses({ ...responses, [q.label]: opt })} data-testid={`yes-no-${opt.toLowerCase()}-${q.label}`}
                className={`px-5 py-2 rounded-xl text-sm font-medium border ${val === opt ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border text-brand-muted'}`}>
                {opt}
              </button>
            ))}
          </div>
        );
      case 'single_choice':
        return (
          <div className="space-y-1.5">
            {(q.options || []).map(opt => (
              <button key={opt} type="button" onClick={() => setResponses({ ...responses, [q.label]: opt })}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm border ${val === opt ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border text-brand-muted'}`}>
                {opt}
              </button>
            ))}
          </div>
        );
      case 'multi_choice':
        return (
          <div className="space-y-1.5">
            {(q.options || []).map(opt => {
              const selected = Array.isArray(val) && val.includes(opt);
              return (
                <button key={opt} type="button" onClick={() => {
                  const arr = Array.isArray(val) ? [...val] : [];
                  if (selected) setResponses({ ...responses, [q.label]: arr.filter(v => v !== opt) });
                  else setResponses({ ...responses, [q.label]: [...arr, opt] });
                }} className={`w-full text-left px-3 py-2 rounded-xl text-sm border ${selected ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border text-brand-muted'}`}>
                  {selected && <Check size={12} className="inline mr-1" />}{opt}
                </button>
              );
            })}
          </div>
        );
      default:
        return <Input value={val} onChange={e => setResponses({ ...responses, [q.label]: e.target.value })} className="rounded-xl border-brand-border" />;
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><p className="text-brand-muted">A carregar...</p></div>;

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col" data-testid="pre-consulta-onboarding">
      {/* Header */}
      <div className="bg-white border-b border-brand-border py-4 px-5 flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-brand-green text-lg">nutrianapereira</h1>
          <p className="text-[10px] text-brand-muted">Questionário pré-consulta</p>
        </div>
        <button onClick={logout} className="text-xs text-brand-muted flex items-center gap-1 hover:text-red-500"><SignOut size={14} /> Sair</button>
      </div>

      {/* Progress */}
      <div className="px-5 py-3">
        <div className="flex gap-2">
          <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-brand-green' : 'bg-brand-border'}`} />
          <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-brand-green' : 'bg-brand-border'}`} />
        </div>
        <p className="text-[10px] text-brand-muted mt-1 text-center">Passo {step} de 2</p>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-4 overflow-y-auto">
        {step === 1 && (
          <div className="max-w-lg mx-auto space-y-5">
            <h2 className="font-heading font-medium text-xl text-brand-text">Condições do programa</h2>
            <div className="bg-white border border-brand-border rounded-2xl p-5 text-sm text-brand-text leading-relaxed whitespace-pre-line" data-testid="conditions-display">
              <Md as="div">{template?.conditions_text || 'Sem condições definidas para este programa.'}</Md>
            </div>
            <label className="flex items-start gap-3 cursor-pointer p-3 bg-white border border-brand-border rounded-2xl" data-testid="accept-conditions">
              <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} className="mt-0.5 rounded border-brand-border w-5 h-5" />
              <span className="text-sm text-brand-text">Li e aceito as condições do programa</span>
            </label>
            <button onClick={() => setStep(2)} disabled={!accepted}
              className="w-full bg-brand-green text-white rounded-full py-3.5 font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid="continue-to-step2">
              Continuar
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="max-w-lg mx-auto space-y-5">
            <h2 className="font-heading font-medium text-xl text-brand-text">Questionário</h2>
            <p className="text-xs text-brand-muted">Preenche as perguntas antes da tua primeira consulta.</p>
            {questions.length === 0 ? (
              <p className="text-sm text-brand-muted py-8 text-center">Sem perguntas configuradas.</p>
            ) : (
              <div className="space-y-4">
                {questions.map((q, i) => (
                  <div key={i} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`onboard-q-${i}`}>
                    <p className="text-sm font-medium text-brand-text mb-2">
                      <Md>{q.label}</Md> {q.required && <span className="text-red-400 text-xs">*</span>}
                    </p>
                    {renderQuestion(q, i)}
                  </div>
                ))}
              </div>
            )}
            <button onClick={handleSubmit} disabled={!allRequiredFilled || submitting}
              className="w-full bg-brand-green text-white rounded-full py-3.5 font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid="submit-onboarding">
              {submitting ? 'A enviar...' : 'Concluir'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
